"use strict";

import SessionManager from "session-mgmt";
import keepAlive from "./session/keep-alive";
import loginFailureHandler from "./session/login-failure-handler";
import redirectAfterLogin from "./session/redirect-after-login";
import token from "./session/token";
import whitelabelUrl from "./whitelabel-url";

let loginUrl = CONFIGURATION.providerAuthUrl;
let fronteggApp;
let loginRequested = false;
let sessionCreated = false;

if (CONFIGURATION.backupAuthEnabled) {
  loginUrl = `${CONFIGURATION.redirectToUri}/login`;
}
token.useCookies();

const readToken = function () {
  return token.load() || null;
};

const SessionPort = {
  read: readToken,
  add: function (app, clientId) {
    const sessionSDK = initSessionSDK();
    fronteggApp = initFronteggApp(clientId, loginUrl);

    keepAlive.forBrowserFreeze(() =>
      login({ loginFrom: "browser freeze", redirect: true }),
    );
    window.fronteggApp = fronteggApp;

    app.ports.sessionAuthorizePort.subscribe(function (options) {
      subscribeToAuth({ redirect: options.redirect });
      loginFailureHandler.log(fronteggApp);
    });

    if (app.ports.sessionRefreshSessionPort) {
      app.ports.sessionRefreshSessionPort.subscribe(function () {
        login({ loginFrom: "refreshing session", redirect: true });
      });
    }

    if (app.ports.logoutPort) {
      app.ports.logoutPort.subscribe(logout);
    }

    if (app.ports.persistBackupAuthTokenPort) {
      app.ports.persistBackupAuthTokenPort.subscribe(function (accessToken) {
        token.set(accessToken);
        dispatchSessionCreatedEvent(accessToken);
      });
    }

    if (app.ports.refreshBackupAuthTokenPort) {
      app.ports.refreshBackupAuthTokenPort.subscribe(function (accessToken) {
        token.set(accessToken);
        app.ports.onAccessTokenUpdatedPort.send(accessToken);
      });
    }

    app.ports.sessionPersistRedirectPort.subscribe(function (url) {
      redirectAfterLogin.persistUrl(url);
    });

    function subscribeToAuth({ redirect = true } = {}) {
      sessionSDK.run({
        app: fronteggApp,
        onExpirationWarning: function () {
          app.ports.onLogoutWarningPort.send(null);
        },
        onUnAuthenticated: function () {
          redirectAfterLogin.persistUrl();
          login({ loginFrom: "unauthenticated", redirect: redirect });
        },
        onAccessTokenChange: onAccessTokenChange,
      });
    }

    function onAccessTokenChange(accessToken) {
      loginRequested = false;
      if (CONFIGURATION.backupAuthEnabled) {
        return;
      }

      const isNewToken = !readToken();
      token.set(accessToken);

      if (isNewToken || !sessionCreated) {
        dispatchSessionCreatedEvent(accessToken);
      } else {
        app.ports.onAccessTokenUpdatedPort.send(readToken());
      }
    }

    function login({ loginFrom, redirect = true } = {}) {
      if (loginRequested || CONFIGURATION.backupAuthEnabled) {
        return;
      }
      if (!CONFIGURATION.skipSSOInit) {
        fronteggApp.ready(() => {
          if (!redirect) {
            fronteggApp.loginWithRedirectV2({
              shouldRedirectToLogin: false,
              firstTime: false,
            });
          } else {
            setTimeout(() => {
              loginRequested = false;
            }, 5000);
            console.log("Attempting to login from", loginFrom);
            loginRequested = true;
            const loginEmail = localStorage.getItem("login-email");
            if (loginEmail) {
              fronteggApp.loginWithRedirect({ login_hint: loginEmail });
              localStorage.removeItem("login-email");
            } else {
              fronteggApp.loginWithRedirect();
            }
          }
        });
      } else {
        if (readToken()) {
          app.ports.onAccessTokenUpdatedPort.send(readToken());
        }
      }
    }

    function logout({ redirect = true } = {}) {
      if (redirect) {
        sessionSDK.logout();
      } else {
        let currentUrl = sessionSDK.postLogoutRedirectURI;

        sessionSDK.postLogoutRedirectURI = window.location.href.replace(
          window.location.origin,
          "",
        );
        sessionSDK.logout();

        sessionSDK.postLogoutRedirectURI = currentUrl;
      }
    }

    function dispatchSessionCreatedEvent(accessToken) {
      const event = {
        detail: {
          accessToken: accessToken,
          redirectToURI: redirectAfterLogin.retrieveUrl(),
        },
      };

      app.ports.onAccessTokenCreatedPort.send({
        accessToken: event.detail.accessToken || readToken(),
        redirectToURI: event.detail.redirectToURI,
      });

      sessionCreated = true;
      redirectAfterLogin.resetUrl();
    }

    function initSessionSDK() {
      return new SessionManager({
        postLogoutRedirectURI: whitelabelUrl.get("/login"),
        loginUrl: loginUrl,
        isDevelopment: CONFIGURATION.debugOrTest,
        logger: true,
        backupAuth: CONFIGURATION.backupAuthEnabled,
        apiUrl: `${CONFIGURATION.apiUrl}/api/`,
        onLogout: function () {
          token.remove();
          app.ports.onSessionLoggedOutPort.send(null);
          redirectAfterLogin.persistUrl();
        },
      });
    }
  },
};

function initFronteggApp(clientId, authUrl) {
  return Frontegg.initialize({
    contextOptions: {
      baseUrl: authUrl,
      clientId: clientId,
      tenantResolver: () => {
        return {
          tenant: whitelabelUrl.setAndRetrieve(),
        };
      },
    },
    customLoader: !!readToken(),
    authOptions: {
      keepSessionAlive: true,
    },
    hostedLoginBox: true,
  });
}

module.exports = SessionPort;
